import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import React, { useEffect, useMemo, useState } from 'react';
import { hasPermission } from '@/types';
import { useNavigate } from 'react-router-dom';
import { leaves as leavesApi, employees as employeesApi } from '@/api/endpoints';
import type { LeaveRequest, LeaveMeta, LeaveTypeCode, LeaveStatusCode, Employee } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { MonthCalendar, type CalendarEvent } from '@/components/MonthCalendar';
import { useAuth } from '@/auth/AuthContext';

/** Token koloru pill → hex (kafelki kalendarza używają inline-style). */
const COLOR_HEX: Record<string, string> = {
  water: '#2563eb', ai: '#7c3aed', earth: '#b45309', amber: '#d97706',
  green: '#059669', red: '#dc2626', gray: '#6b7280',
};
const hex = (c: string) => COLOR_HEX[c] ?? '#6b7280';

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDays = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ','));
/** Poprawna forma po liczbie dni: „1 dzień", „2 dni", „0,5 dnia". */
const dni = (n: number) => `${fmtDays(n)} ${n === 1 ? 'dzień' : Number.isInteger(n) ? 'dni' : 'dnia'}`;

function openDownload(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Firmowy plan urlopów — kalendarz miesięczny ze wszystkimi nieobecnościami pracowników. Kliknięcie
 * dnia (OWNER/ADMIN/OFFICE) otwiera dodawanie urlopu; kliknięcie wpisu przenosi na kartę pracownika.
 */
export function UrlopyKalendarzPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canManage = hasPermission(user, 'HR_MANAGE');

  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [list, setList] = useState<LeaveRequest[]>([]);
  const [meta, setMeta] = useState<LeaveMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [modal, setModal] = useState<{ prefillDate: string } | null>(null);
  const [justAdded, setJustAdded] = useState<LeaveRequest | null>(null);   // do pobrania wniosku po dodaniu

  const range = useMemo(() => {
    const y = month.getFullYear(), m = month.getMonth();
    const startWeekday = (new Date(y, m, 1).getDay() + 6) % 7;
    return { from: ymd(new Date(y, m, 1 - startWeekday)), to: ymd(new Date(y, m, 1 - startWeekday + 41)) };
  }, [month]);

  const reload = () => { leavesApi.range(range.from, range.to).then(setList).catch(e => setErr(e?.message ?? String(e))); };
  useEffect(reload, [range.from, range.to]);
  useEffect(() => { leavesApi.meta().then(setMeta).catch(e => setErr('Nie udało się wczytać słowników urlopów: ' + (e?.message ?? String(e)))); }, []);

  const events: CalendarEvent[] = list
    .filter(l => l.status !== 'CANCELLED')
    .map(l => ({
      id: l.id, title: l.employeeName, startDate: l.startDate, endDate: l.endDate,
      color: hex(l.color),
      tooltip: `${l.employeeName} — ${l.leaveTypeName} (${dni(l.workingDays)})`
        + (l.signedAt ? ` · wniosek podpisany ${l.signedAt}` : '')
        + (l.note ? ' · ' + l.note : ''),
    }));

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Plan urlopów</h1>
          <div className="text-ink-400 text-[13px]">Nieobecności pracowników — wypoczynkowy, na żądanie, dni za święto i inne</div>
        </div>
        {canManage && (
          <button onClick={() => setModal({ prefillDate: ymd(new Date()) })} disabled={!meta} title={meta ? '' : 'Ładowanie słowników…'} className="btn btn-primary btn-sm disabled:opacity-50">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowy urlop
          </button>
        )}
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      {/* Po dodaniu urlopu — wniosek do wydruku i podpisu przez pracownika. */}
      {justAdded && (
        <div className="mb-3 flex items-center gap-3 flex-wrap rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-ink-700">
          <Icon name="check" className="w-4 h-4 text-green-600 shrink-0"/>
          <span>
            Dodano: <strong>{justAdded.employeeName}</strong> · {justAdded.leaveTypeName} ·{' '}
            <span className="font-mono">{justAdded.startDate}–{justAdded.endDate}</span> ({dni(justAdded.workingDays)})
          </span>
          <button onClick={() => openDownload(employeesApi.leave.wniosekPdfUrl(justAdded.employeeId, justAdded.id))}
                  className="btn btn-primary btn-sm ml-auto">
            <Icon name="download" className="w-3.5 h-3.5"/> Wniosek urlopowy (PDF)
          </button>
          <button onClick={() => setJustAdded(null)} className="btn btn-ghost btn-icon text-ink-400" title="Zamknij">
            <Icon name="x" className="w-3.5 h-3.5"/>
          </button>
        </div>
      )}

      <MonthCalendar
        month={month}
        events={events}
        onPrevMonth={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        onNextMonth={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        onToday={() => { const n = new Date(); setMonth(new Date(n.getFullYear(), n.getMonth(), 1)); }}
        onDayClick={iso => { if (canManage) setModal({ prefillDate: iso }); }}
        onEventClick={ev => { const l = list.find(x => x.id === ev.id); if (l) nav('/kadry/' + l.employeeId); }}
      />

      {/* Legenda typów */}
      {meta && (
        <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-ink-500">
          {meta.leaveTypes.map(t => (
            <span key={t.id} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: hex(t.color) }}/>{t.name}
            </span>
          ))}
        </div>
      )}

      {modal && meta && (
        <NewLeaveModal prefillDate={modal.prefillDate} meta={meta}
                       onClose={() => setModal(null)}
                       onSaved={created => { setModal(null); setJustAdded(created); reload(); }}/>
      )}
    </div>
  );
}

function NewLeaveModal({ prefillDate, meta, onClose, onSaved }: {
  prefillDate: string; meta: LeaveMeta; onClose: () => void; onSaved: (created: LeaveRequest) => void;
}) {
  const [people, setPeople] = useState<Employee[]>([]);
  const [employeeId, setEmployeeId] = useState<number | ''>('');
  const [leaveType, setLeaveType] = useState<LeaveTypeCode>('WYPOCZYNKOWY');
  const [status, setStatus] = useState<LeaveStatusCode>('APPROVED');
  const [startDate, setStartDate] = useState(prefillDate);
  const [endDate, setEndDate] = useState(prefillDate);
  const [halfDay, setHalfDay] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { employeesApi.list('ACTIVE').then(setPeople).catch(() => {}); }, []);
  const singleDay = startDate === endDate;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (employeeId === '') { setErr('Wybierz pracownika'); return; }
    if (!startDate || !endDate) { setErr('Podaj zakres dat'); return; }
    if (endDate < startDate) { setErr('Data zakończenia nie może być wcześniejsza niż rozpoczęcia'); return; }
    setBusy(true); setErr(null);
    employeesApi.leave.add(Number(employeeId), {
      leaveType, status, startDate, endDate, halfDay: singleDay && halfDay, note: note.trim() || null,
    }).then(onSaved).catch(e2 => { setErr(e2?.message ?? String(e2)); setBusy(false); });
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { employeeId, leaveType, status, startDate, endDate, halfDay, note }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[560px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">Nowy urlop / nieobecność</h2></div>
        <div className="p-5 grid gap-3">
          <label className="field">
            <span className="field-label">Pracownik *</span>
            <SearchableSelect value={employeeId === '' ? '' : String(employeeId)} onChange={v => setEmployeeId(v ? Number(v) : '')}>
              <option value="">— wybierz pracownika —</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Typ *</span>
            <SearchableSelect value={leaveType} onChange={v => setLeaveType(v as LeaveTypeCode)}>
              {meta.leaveTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </SearchableSelect>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Od *</span>
              <input type="date" required value={startDate} onChange={e => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} className="input"/>
            </label>
            <label className="field">
              <span className="field-label">Do *</span>
              <input type="date" required value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)} className="input"/>
            </label>
          </div>
          {singleDay && (
            <label className="flex items-center gap-2 text-[13px] text-ink-700">
              <input type="checkbox" checked={halfDay} onChange={e => setHalfDay(e.target.checked)}/> Pół dnia (0,5)
            </label>
          )}
          <label className="field">
            <span className="field-label">Status</span>
            <SearchableSelect value={status} onChange={v => setStatus(v as LeaveStatusCode)}>
              {meta.leaveStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Uwagi</span>
            <textarea value={note} onChange={e => setNote(e.target.value)} className="textarea" rows={2}/>
          </label>
          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary"><Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
